import { useEffect, useState } from 'react'
import PropTypes from 'prop-types'
import ReactTooltip from 'react-tooltip'
import { ArrowLeft, CurrencyDollar, Circle, ExclamationMark, Trash } from '@phosphor-icons/react'
import { useTranslation } from 'react-i18next'

import { formatDollars } from '@/utils'
import { Badge } from '@/components/ui/badge'

import SelectField from '../../FormLegacy/FormFieldsV2/SelectField'
import TextField from '../../FormLegacy/FormFieldsV2/TextField'
import CheckboxField from '../../FormLegacy/FormFieldsV2/CheckboxField'
import RadioField from '../../FormLegacy/FormFieldsV2/RadioField'
import ConfirmPopup from '../../Popups/ConfirmPopup'
import Dashboard from '../../Dashboard'
import Loader from '../../Loader/Loader'
import api from '../../../api/api'
import { useSnackbar } from '../../../helpers/hooks/useSnackbar'
import { APP_COLORS } from '../../../helpers/constants'

import ConfirmAutopaySetup from './ConfirmAutopaySetup'
import StripePaymentMethodModal from './StripePaymentMethodModal'
import PaymentHistory from './PaymentHistory'
import { BankCardAddIcon, CardBrandIcon, StripeIcon } from './PaymentIcons'
import { getChargesPayable, getChargesProcessing, getDaysOfTheMonth, methodLabel } from './helpers'

const buildForm = (autopaySettings) => ({
  isAutoPayEnabled: autopaySettings?.isAutoPayEnabled ? 'Yes' : 'No',
  dayOfTheMonth: autopaySettings?.dayOfTheMonth || 1,
  amount: autopaySettings?.amount ? autopaySettings.amount / 100 : '',
})

const PayRentViewYardi = ({
  autopaySettings,
  isLoading,
  transactions,
  setPayNowMode,
  paymentSettings,
  refetchAutopay,
  paymentHistory,
  paymentHistoryError,
  isPmsHistoryUnavailable,
  blockPayment,
  blockPaymentMessage,
}) => {
  const { displaySnackbar } = useSnackbar()
  const { t } = useTranslation(['organisation', 'common'])
  const [isAutopayManageMode, setAutopayManageMode] = useState(false)
  const [isReminderWindowOpen, setReminderWindowOpen] = useState(false)
  const [showConfirmSetup, setShowConfirmSetup] = useState(false)
  const [showMethodModal, setShowMethodModal] = useState(false)
  const [isSubmitting, setSubmitting] = useState(false)

  const [form, setForm] = useState(buildForm(autopaySettings))
  const [autoPayTerms, setAutoPayTerms] = useState(false)
  // set once the resident captures a NEW method via Stripe; null means "reuse the saved one"
  const [setupIntentId, setSetupIntentId] = useState(null)
  const [newMethod, setNewMethod] = useState(null)
  const [paymentMethods, setPaymentMethods] = useState([])
  const [methodToDelete, setMethodToDelete] = useState(null)
  const [isDeletingMethod, setIsDeletingMethod] = useState(false)
  const [payType, setPayType] = useState('Credit Card')
  const [newMethodPayType, setNewMethodPayType] = useState(null)
  // id of the saved method autopay will charge; null while a freshly-captured method (setupIntentId) is pending
  const [selectedMethodId, setSelectedMethodId] = useState(null)

  const fetchPaymentMethods = async () => {
    try {
      const { data } = await api.getPaymentMethods()
      setPaymentMethods(data || [])
    } catch {
      setPaymentMethods([])
    }
  }

  useEffect(() => {
    setForm(buildForm(autopaySettings))
    setAutoPayTerms(!!autopaySettings?.autoPayTerms)
    setSetupIntentId(null)
    setNewMethod(null)
    setSelectedMethodId(autopaySettings?.paymentMethod?.id ?? null)
    setPayType(
      autopaySettings?.paymentMethod?.methodType === 'ACH' ? 'Bank Account' : 'Credit Card',
    )
  }, [autopaySettings, isAutopayManageMode])

  useEffect(() => {
    if (isAutopayManageMode) fetchPaymentMethods()
  }, [isAutopayManageMode])

  const isEnabling = form.isAutoPayEnabled === 'Yes'
  const hasMethod = !!setupIntentId || !!selectedMethodId
  // ACH methods still have a `.brand` (the bank's name, e.g. "STRIPE TEST BANK") — `methodType`
  // is the reliable field, same as the `methodType !== 'ACH'` check PayNowView uses. A
  // freshly-captured method takes its type from the refetched method list — never from the
  // active radio, or switching radios would show it under the wrong type. Only if that refetch
  // failed (newMethod null) fall back to the radio that was active when it was added.
  const newMethodIsBank = newMethod
    ? newMethod.methodType === 'ACH'
    : newMethodPayType === 'Bank Account'
  const isFormValid = isEnabling
    ? Number(form.amount) >= 1 && !!Number(form.dayOfTheMonth) && autoPayTerms && hasMethod
    : true

  const totalPayable = getChargesPayable(transactions)
  const totalProcessing = getChargesProcessing(transactions)
  const hasProcessingAmount = !isLoading && totalProcessing > 0
  const isFullyProcessing = totalProcessing > 0 && totalPayable === 0

  const submitUpdate = async () => {
    if (isSubmitting) return
    setSubmitting(true)
    try {
      await api.setClientAutopay({
        isAutoPayEnabled: isEnabling,
        isFullAmount: false,
        amount: Math.round(Number(form.amount) * 100),
        dayOfTheMonth: Number(form.dayOfTheMonth),
        autoPayTerms,
        // the API rejects both ids together: setupIntentId for a new method, paymentMethodId for a saved
        // one; omit both when keeping the current method
        ...(setupIntentId
          ? { setupIntentId }
          : selectedMethodId && selectedMethodId !== autopaySettings?.paymentMethod?.id
            ? { paymentMethodId: selectedMethodId }
            : {}),
      })
      displaySnackbar(
        isEnabling
          ? t('organisation:payments.payRentYardi.autopayUpdated')
          : t('organisation:payments.payRentYardi.autopayTurnedOff'),
      )
      await refetchAutopay?.()
      setAutopayManageMode(false)
    } catch (error) {
      displaySnackbar(
        error?.response?.data?.message || t('organisation:payments.payRentYardi.updateFailed'),
        true,
      )
    } finally {
      setSubmitting(false)
    }
  }

  const onConfirm = () => {
    // enabling shows the review/confirm dialog; disabling just submits
    if (isEnabling) setShowConfirmSetup(true)
    else submitUpdate()
  }

  const handleDeleteMethod = async () => {
    setIsDeletingMethod(true)
    try {
      await api.deletePaymentMethod(methodToDelete.id)
      if (selectedMethodId === methodToDelete.id) setSelectedMethodId(null)
      await Promise.all([refetchAutopay?.(), fetchPaymentMethods()])
      displaySnackbar(t('organisation:payments.toasts.paymentMethodDeleted'))
    } catch (error) {
      displaySnackbar(
        error?.response?.data?.message || t('organisation:payments.errors.deleteMethodFailed'),
        true,
      )
    } finally {
      setIsDeletingMethod(false)
      setMethodToDelete(null)
    }
  }

  // paymentMethods loads async in manage mode — until then fall back to the method autopay already uses
  const savedMethods = paymentMethods.length
    ? paymentMethods
    : [autopaySettings?.paymentMethod].filter(Boolean)
  const methodsOfType = (isBank) =>
    savedMethods.filter((method) => (method.methodType === 'ACH') === isBank)

  const selectSavedMethod = (id) => {
    setSelectedMethodId(id)
    setSetupIntentId(null)
    setNewMethod(null)
  }

  const renderMethods = (isBank) => {
    const showNewMethod = !!setupIntentId && newMethodIsBank === isBank
    const methods = showNewMethod ? [] : methodsOfType(isBank)
    if (!showNewMethod && !methods.length && !(setupIntentId && newMethodIsBank !== isBank)) {
      return (
        <div className="autopay-setup__no-method">
          {t('organisation:payments.payRentYardi.noPaymentMethod')}
        </div>
      )
    }
    return (
      <div className="saved-methods-list">
        {showNewMethod && (
          <div className="saved-method-summary">
            {isBank ? <StripeIcon /> : <CardBrandIcon brand={newMethod?.brand} />}
            <span className="saved-method-summary__label">
              {newMethod
                ? methodLabel(t, newMethod)
                : t('organisation:payments.payRentYardi.newMethodAdded')}
            </span>
          </div>
        )}
        {methods.map((method) => {
          const isSelected = selectedMethodId === method.id
          return (
            <div key={method.id} className="credit-card-option">
              <div
                className={`credit-card-option__details is-selectable ${isSelected ? 'is-selected' : ''}`}
                onClick={() => selectSavedMethod(method.id)}
              >
                <div className="credit-card-option__info">
                  {isBank ? <StripeIcon /> : <CardBrandIcon brand={method.brand} />}
                  <span className="saved-method-summary__label">{methodLabel(t, method)}</span>
                </div>
                <RadioField
                  name={`autopay-saved-${isBank ? 'bank' : 'card'}-choice`}
                  value={method.id}
                  checked={isSelected}
                  label=""
                  onChange={() => selectSavedMethod(method.id)}
                />
              </div>
              <button
                type="button"
                className="delete-method-button"
                aria-label={t('organisation:payments.payNow.deleteMethod')}
                onClick={() => setMethodToDelete(method)}
              >
                <Trash size={16} />
              </button>
            </div>
          )
        })}
      </div>
    )
  }

  if (isAutopayManageMode) {
    return (
      <Dashboard.Container className="ctp-payments">
        <div className="ctp-payments__header">
          <button className="button button-bordered" onClick={() => setAutopayManageMode(false)}>
            <ArrowLeft size={20} />
            {t('common:actions.back')}
          </button>
          {t('organisation:payments.payRentYardi.autoPaymentSetupTitle')}
        </div>
        <div className="ctp-payments__body autopay-setup">
          <div className="autopay-setup__section">
            <div className="simple-header">
              {t('organisation:payments.payRentYardi.autopayProfileHeading')}
            </div>
            <div className="payment-method-wrap">
              <div className="autopay-setup__fields">
                <SelectField
                  name="autopaymentEnabled"
                  label={t('organisation:payments.payRentYardi.enableAutopayQuestion')}
                  options={[
                    { label: t('common:booleans.yes'), value: 'Yes' },
                    { label: t('common:booleans.no'), value: 'No' },
                  ]}
                  onChange={(e) => setForm({ ...form, isAutoPayEnabled: e.target.value })}
                  value={form.isAutoPayEnabled}
                />
                <SelectField
                  name="dayOfMonth"
                  label={t('organisation:payments.payRentYardi.dayOfMonthLabel')}
                  options={getDaysOfTheMonth(t)}
                  onChange={(e) => setForm({ ...form, dayOfTheMonth: e.target.value })}
                  value={form.dayOfTheMonth}
                />
              </div>
            </div>
          </div>

          <div className="autopay-setup__section">
            <div className="simple-header">
              {t('organisation:payments.payRentYardi.paymentAmountLabel')}
            </div>
            <div className="payment-method-wrap">
              <TextField
                name="paymentAmount"
                type="number"
                leftIcon={<CurrencyDollar size={20} style={{ top: 8 }} />}
                onChange={(e) => setForm({ ...form, amount: e.target.value })}
                value={form.amount}
              />
            </div>
          </div>

          <div className="autopay-setup__section">
            <div className="simple-header">
              {t('organisation:payments.payNow.choosePaymentMethodLabel')}
            </div>
            <div className="payment-method-wrap">
              <div className="flex-between">
                <RadioField
                  name="autopay-credit-card-radio"
                  value="Credit Card"
                  checked={payType === 'Credit Card'}
                  label={t('organisation:payments.payNow.creditCard')}
                  onChange={() => setPayType('Credit Card')}
                />
                <div className="card-logos">
                  <img src="/images/Maestro.png" alt="Maestro" />
                  <img src="/images/visa.png" alt="Visa" />
                </div>
              </div>
              {payType === 'Credit Card' && (
                <div className="credit-card-options">
                  <div className="options-divider" />
                  {renderMethods(false)}
                  <button
                    type="button"
                    className="add-method-button"
                    onClick={() => setShowMethodModal(true)}
                  >
                    <BankCardAddIcon />
                    {methodsOfType(false).length || (setupIntentId && !newMethodIsBank)
                      ? t('organisation:payments.payRentYardi.updatePaymentMethod')
                      : t('organisation:payments.payRentYardi.addCreditCard')}
                  </button>
                </div>
              )}
            </div>
            <div className="payment-method-wrap">
              <div className="flex-between">
                <RadioField
                  name="autopay-bank-account-radio"
                  value="Bank Account"
                  checked={payType === 'Bank Account'}
                  label={t('organisation:payments.payNow.bankAccount')}
                  onChange={() => setPayType('Bank Account')}
                />
                <div className="grey-header">
                  {t('organisation:payments.payNow.allBanksSupported')}
                </div>
              </div>
              {payType === 'Bank Account' && (
                <div className="credit-card-options">
                  <div className="options-divider" />
                  {renderMethods(true)}
                  <button
                    type="button"
                    className="add-method-button"
                    onClick={() => setShowMethodModal(true)}
                  >
                    <BankCardAddIcon />
                    {t('organisation:payments.payNow.addBankAccount')}
                  </button>
                </div>
              )}
            </div>
          </div>

          <CheckboxField
            name="agreement"
            value={autoPayTerms}
            onChange={(e) => setAutoPayTerms(e.target.checked)}
            label={t('organisation:payments.payRentYardi.agreeToTerms')}
          />
          <button
            disabled={!isFormValid || isSubmitting}
            className={`button button-black autopay-setup__submit ${isSubmitting ? 'is-loading' : ''}`}
            onClick={onConfirm}
          >
            {t('organisation:payments.payRentYardi.updateProfile')}
          </button>
        </div>
        <ConfirmAutopaySetup
          isOpen={showConfirmSetup}
          data={{ date: Number(form.dayOfTheMonth), amount: form.amount }}
          onClose={() => setShowConfirmSetup(false)}
          onSubmit={() => {
            setShowConfirmSetup(false)
            submitUpdate()
          }}
        />
        <StripePaymentMethodModal
          createSetupIntent={api.createSetupIntent}
          isVisible={showMethodModal}
          preferredType={payType === 'Bank Account' ? 'us_bank_account' : 'card'}
          onClose={() => setShowMethodModal(false)}
          onSaved={async (id) => {
            const existingIds = paymentMethods.map((method) => method.id)
            try {
              const { data } = await api.getPaymentMethods()
              const methods = data || []
              setPaymentMethods(methods)
              setNewMethod(methods.find((method) => !existingIds.includes(method.id)) || null)
            } catch {
              setNewMethod(null)
            }
            setSetupIntentId(id)
            setSelectedMethodId(null)
            setNewMethodPayType(payType)
            setShowMethodModal(false)
            displaySnackbar(t('organisation:payments.toasts.paymentMethodSaved'))
          }}
        />
        {methodToDelete && (
          <ConfirmPopup
            isOpen
            className={methodToDelete.usedByAutoPay ? 'is-caution' : ''}
            title={t('common:confirmPopup.confirmation')}
            body={t(
              methodToDelete.usedByAutoPay
                ? 'organisation:payments.payNow.deleteMethodAutopayWarning'
                : 'organisation:payments.payNow.deleteMethodBody',
              { methodLabel: methodLabel(t, methodToDelete) },
            )}
            confirmText={t('common:actions.delete')}
            cancelText={t('common:actions.cancel')}
            isLoading={isDeletingMethod}
            confirmCallback={handleDeleteMethod}
            cancelCallback={() => setMethodToDelete(null)}
          />
        )}
      </Dashboard.Container>
    )
  }

  const autopayEnabled = !!autopaySettings?.isAutoPayEnabled
  const canManageAutopay = !!paymentSettings?.allowAutopay

  return (
    <Dashboard.Container className="ctp-payments">
      <div className="ctp-payments__header">{t('organisation:payments.payRentYardi.header')}</div>
      <div className="ctp-payments__body">
        <div className="ctp-payments__body__company-section">
          <div className="left-side">
            <div className="total-header">{t('organisation:payments.payRentYardi.totalDue')}</div>
            <div className="flex-row">
              <div className="total-cost">
                {isLoading ? (
                  <Loader className="loader" />
                ) : (
                  formatDollars(totalPayable, { zeroPrice: true })
                )}
              </div>
              {(blockPaymentMessage || hasProcessingAmount) && (
                <div className="flex-row payment-message-wrap">
                  <ExclamationMark size={22} weight="bold" />
                  <div>
                    {blockPaymentMessage && (
                      <div className="block-payment-message">{blockPaymentMessage}</div>
                    )}
                    {hasProcessingAmount && (
                      <div className="block-payment-message">
                        {t('organisation:payments.processing.inProgress', {
                          amount: totalProcessing,
                        })}
                      </div>
                    )}
                  </div>
                </div>
              )}
            </div>
          </div>
          <div className="right-side">
            {canManageAutopay && (
              <div className="manage-autopay">
                <button
                  className={`button button-secondary ${isLoading ? 'is-loading' : ''}`}
                  onClick={() => setAutopayManageMode(true)}
                >
                  {t('organisation:payments.payRentYardi.manageAutopay')}
                </button>
                <div className="autopay-mark">
                  <Circle
                    size={13}
                    color={autopayEnabled ? APP_COLORS.green500 : APP_COLORS.red500}
                    weight="fill"
                  />{' '}
                  {t('organisation:payments.payRentYardi.autopayStatus', {
                    status: autopayEnabled ? t('common:booleans.yes') : t('common:booleans.no'),
                  })}
                </div>
              </div>
            )}
            {isFullyProcessing && !isLoading ? (
              <Badge color="orange" size="medium">
                {t('organisation:payments.processing.badge')}
              </Badge>
            ) : (
              <button
                className={`button button-primary ${isLoading ? 'is-loading' : ''}`}
                style={blockPayment ? { backgroundColor: APP_COLORS.black500 } : {}}
                data-tip
                data-for="payNowButton"
                onClick={
                  blockPayment
                    ? null
                    : autopayEnabled && canManageAutopay
                      ? () => setReminderWindowOpen(true)
                      : setPayNowMode
                }
                disabled={!totalPayable}
              >
                {t('organisation:payments.payNow.title')}
              </button>
            )}
            {blockPaymentMessage && (
              <ReactTooltip multiline={true} id="payNowButton" place="top" effect="solid">
                {blockPaymentMessage ||
                  t('organisation:payments.payRentYardi.paymentProcessingMessage')}
              </ReactTooltip>
            )}
          </div>
        </div>
        <PaymentHistory
          history={paymentHistory}
          isLoading={isLoading}
          error={paymentHistoryError}
          isPmsHistoryUnavailable={isPmsHistoryUnavailable}
        />
      </div>
      <ConfirmPopup
        title={t('organisation:payments.payRentYardi.reminder.title')}
        body={t('organisation:payments.payRentYardi.reminder.body', {
          day:
            getDaysOfTheMonth(t).find((day) => day.value === autopaySettings?.dayOfTheMonth)
              ?.label || t('organisation:payments.payRentYardi.reminder.selectedDayFallback'),
        })}
        cancelText={t('common:actions.cancel')}
        confirmText={t('organisation:payments.payNow.title')}
        isOpen={isReminderWindowOpen}
        cancelCallback={() => setReminderWindowOpen(false)}
        confirmCallback={() => {
          setReminderWindowOpen(false)
          setPayNowMode()
        }}
        className={'is-reminder'}
      />
    </Dashboard.Container>
  )
}

PayRentViewYardi.propTypes = {
  autopaySettings: PropTypes.object,
  isLoading: PropTypes.bool,
  transactions: PropTypes.array,
  setPayNowMode: PropTypes.func,
  paymentSettings: PropTypes.object,
  refetchAutopay: PropTypes.func,
  paymentHistory: PropTypes.array,
  paymentHistoryError: PropTypes.string,
  isPmsHistoryUnavailable: PropTypes.bool,
  blockPayment: PropTypes.bool,
  blockPaymentMessage: PropTypes.string,
}

export default PayRentViewYardi
